import React, { useEffect, useState } from 'react'
import styles from './NoCallsModal.module.css'
import AnimatedButton from '../AnimatedButton/AnimatedButton'
import NewModal from '../NewModal/NewModal'

/**
 * No-calls onboarding modal — shown when the agent hasn't taken any call yet
 * (callCount === 0). Deliberately has NO close (×) button; the user must
 * either take an action or use the "Skip" link.
 */
const NoCallsModal = ({
    callCount = 0,
    agentId,
    agentAvatar = "/svg/receptionist_profile.png",
    // Host passes false until the intro tour finishes (tour goes first).
    onboardingReady = true,
    // Gates the whole modal. The call-forwarding onboarding only makes sense
    // for paid agents (free/demo users have no live number to forward), so the
    // host passes isPaidPlanAgent here. Defaults true to preserve behaviour for
    // any other caller.
    eligible = true,
    onTestForwarding,
    onNumberPublished,
    onInvitePeople,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const hasNoCalls = Number(callCount) === 0;

    // Use the visitor's local calendar date, not a rolling 24-hour window,
    // so the modal is eligible once on each new day. Scope the marker to the
    // selected agent so one agent's popup does not suppress another's.
    useEffect(() => {
        if (!agentId || !hasNoCalls || !onboardingReady || !eligible) {
            setIsOpen(false);
            return;
        }

        const now = new Date();
        const today = [
            now.getFullYear(),
            String(now.getMonth() + 1).padStart(2, '0'),
            String(now.getDate()).padStart(2, '0'),
        ].join('-');
        const storageKey = `noCallsModal.lastShown.${agentId}`;

        try {
            if (localStorage.getItem(storageKey) === today) {
                setIsOpen(false);
                return;
            }
            // Record on actual first display, not only on Skip. This prevents
            // every action/navigation back to dashboard from reopening it.
            localStorage.setItem(storageKey, today);
        } catch (_e) {
            // If storage is unavailable, still allow the modal for this mount.
        }

        setIsOpen(true);
    }, [agentId, hasNoCalls, onboardingReady, eligible]);

    return (
        <NewModal
            isOpen={isOpen && hasNoCalls && onboardingReady && eligible}
            onClose={() => { }}
            title={null}
            height="auto"
            showCloseBtn={false}
            bodyMaxHeight="90vh"
        >
            <div className={styles.noCallsModal}>
                <div className={styles.noCallsHead}>
                    <div className={styles.noCallsAvatarWrap}>
                        <img src={agentAvatar} alt="Agent" className={styles.noCallsAvatar} />
                        <span className={styles.noCallsAvatarBadge} />
                    </div>
                    <h2 className={styles.noCallsTitle}>
                        <span className={styles.noCallsHighlight}>No calls</span> yet
                        <br />
                        Let's fix that.
                    </h2>
                </div>

                <p className={styles.noCallsDesc}>
                    Your AI is ready! Now let's make sure your calls are coming through.
                </p>

                <div className={styles.noCallsList}>
                    <button type="button" className={styles.noCallsRow} onClick={onTestForwarding}>
                        <span className={styles.noCallsRowIcon}>
                            <img src="/svg/call_forwarding.svg" alt="Test Forwarding" />
                        </span>
                        <span className={styles.noCallsRowText}>
                            Test your <strong>call forwarding</strong>
                        </span>
                        <span className={styles.noCallsChevron}>›</span>
                    </button>

                    <button type="button" className={styles.noCallsRow} onClick={onNumberPublished}>
                        <span className={styles.noCallsRowIcon}>
                            <img src="/svg/number_publ.svg" alt="Number Published" />
                        </span>
                        <span className={styles.noCallsRowText}>
                            Make sure your <strong>number is published</strong>
                        </span>
                        <span className={styles.noCallsChevron}>›</span>
                    </button>

                    <button type="button" className={styles.noCallsRow} onClick={onInvitePeople}>
                        <span className={styles.noCallsRowIcon}>
                            <img src="/svg/invite_people.svg" alt="Invite People" />
                        </span>
                        <span className={styles.noCallsRowText}>
                            <strong>Invite people</strong> to call your business
                        </span>
                        <span className={styles.noCallsChevron}>›</span>
                    </button>
                </div>

                <AnimatedButton
                    label="Test My Forwarding"
                    onClick={onTestForwarding}
                    positionType="relative"
                    width="100%"
                    bottom="0"
                />

                <button
                    type="button"
                    className={styles.noCallsSkip}
                    onClick={() => setIsOpen(false)}
                >
                    Skip — I'll set this up later
                </button>
            </div>
        </NewModal>
    )
}

export default NoCallsModal
